import React from 'react';
import {
  Box, Container, Typography, Accordion, AccordionSummary, AccordionDetails, Chip, Stack,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import seoData from '../seo/toolSeoData.json';
import toolBodies from '../content/toolBodies.json';
import Markdown from './Markdown';

const byRoute = new Map(seoData.tools.map((tool) => [tool.route, tool]));

const headingSx = {
  fontSize: { xs: '1.3rem', md: '1.6rem' },
  fontWeight: 700,
  mb: 2.5,
};

/**
 * SEO content sections shared by every tool page: visible HowTo steps, FAQ, and
 * related-tools links, all driven by src/seo/toolSeoData.json — the same data
 * scripts/inject-seo.js bakes into the prerendered HTML as HowTo/FAQPage JSON-LD.
 * Keeping the structured data and the visible content on one source is what makes
 * the rich results policy-safe.
 */
export default function ToolSeoSections() {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const tool = pathname === '/' ? null : byRoute.get(pathname.replace(/\/+$/, '') || pathname);

  if (!tool) return null;

  const body = toolBodies[tool.route];

  return (
    <Box component="section" sx={{ background: 'linear-gradient(180deg, transparent, rgba(99,102,241,0.03))' }}>
      <Container maxWidth="md" sx={{ py: { xs: 5, md: 8 } }}>
        {/* Long-form, tool-specific explainer (Markdown source in content/tools/) */}
        {body && (
          <Box sx={{ mb: { xs: 5, md: 7 } }}>
            <Markdown headingShift={1}>{body}</Markdown>
          </Box>
        )}

        {/* HowTo steps */}
        <Typography variant="h2" sx={headingSx}>{t('common.how_title')}</Typography>
        <Box component="ol" sx={{ pl: 3, m: 0, mb: 5 }}>
          {tool.steps.map((step, i) => (
            <Typography key={i} component="li" color="text.secondary" sx={{ mb: 1, lineHeight: 1.7 }}>
              {step}
            </Typography>
          ))}
        </Box>

        {/* FAQ — visible text matches the FAQPage JSON-LD injected at build time */}
        <Typography variant="h2" sx={headingSx}>{t('common.faq_title')}</Typography>
        <Box mb={5}>
          {tool.faqs.map((f, i) => (
            <Accordion key={i} elevation={0} disableGutters
              sx={{ mb: 1.5, borderRadius: '12px !important', border: '1px solid rgba(99,102,241,0.12)', '&:before': { display: 'none' } }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600} component="h3" sx={{ fontSize: '1rem' }}>{f.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography color="text.secondary" sx={{ lineHeight: 1.7 }}>{f.a}</Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>

        {/* Related tools */}
        <Typography variant="h2" sx={headingSx}>{t('common.related_title')}</Typography>
        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap>
          {tool.related.map((route) => {
            const target = byRoute.get(route);
            if (!target) return null;
            return (
              <Chip
                key={route}
                component={RouterLink}
                to={route}
                clickable
                label={target.title}
                sx={{
                  borderRadius: 3, fontWeight: 500, px: 0.5,
                  background: 'rgba(99,102,241,0.08)', color: 'primary.main',
                  '&:hover': { background: 'rgba(99,102,241,0.16)' },
                }}
              />
            );
          })}
        </Stack>
      </Container>
    </Box>
  );
}
